import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import InlineSVG from "react-inlinesvg";
import styled from "styled-components/macro";

import Auth from "src/Auth";
import { decodePlainTextFile } from "src/lib/decodePlainTextFile";
import { StyleGuideSecondaryButton } from "./StyleGuideButtons";

const STYLE_SAMPLE_WORD_LIMIT = 998;

interface AddNewStyleModalProps {
  projectId?: string;
  mode?: "modal" | "embedded";
  onClose: () => void;
  onSkip: () => void;
  onContinueWithSample: (sample: string) => void;
  onPasteText: () => void;
}

export function AddNewStyleModal({
  projectId,
  mode = "modal",
  onClose,
  onSkip,
  onContinueWithSample,
  onPasteText,
}: AddNewStyleModalProps) {
  const [sampleText, setSampleText] = useState("");
  const [sampleFileName, setSampleFileName] = useState("");
  const [sampleError, setSampleError] = useState("");
  const [isConvertingSample, setIsConvertingSample] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const trimmedSampleText = sampleText.trim();

  const limitSampleToFirstWords = (text: string, maxWords = STYLE_SAMPLE_WORD_LIMIT) => {
    let wordCount = 0;
    let endIndex = 0;
    for (const match of text.matchAll(/\S+/g)) {
      wordCount += 1;
      endIndex = (match.index ?? 0) + match[0].length;
      if (wordCount === maxWords) {
        return text.slice(0, endIndex).trim();
      }
    }

    return text.trim();
  };

  const readSampleFile = async (file: File) => {
    setIsConvertingSample(true);
    setSampleText("");
    setSampleFileName("");
    setSampleError("");

    try {
      let convertedText = "";
      if (projectId) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("projectId", projectId);
        formData.append("writingSample", "true");
        const result = await Auth.postFilesWithIdToken("/api/convert", { projectId }, formData);
        convertedText = typeof result?.text === "string" ? result.text : "";
      } else if (file.type === "text/plain" || /\.txt$/i.test(file.name)) {
        convertedText = await decodePlainTextFile(file);
      }

      if (!convertedText.trim()) {
        throw new Error("No text extracted from uploaded style sample");
      }

      const limitedSampleText = limitSampleToFirstWords(convertedText);
      setSampleText(limitedSampleText);
      setSampleFileName(file.name);
      setIsConvertingSample(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onContinueWithSample(limitedSampleText);
    } catch {
      setSampleError("We couldn't extract text from that file. Try another file or paste text instead.");
      setIsConvertingSample(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    void readSampleFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      void readSampleFile(file);
    }
  };

  const continueWithSample = () => {
    if (!trimmedSampleText) return;
    onContinueWithSample(limitSampleToFirstWords(trimmedSampleText));
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const content = (
    <StyleModalOverlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-new-style-title"
      data-testid="project-top-bar-add-style-modal"
      $mode={mode}
    >
      <AddStyleModalSurface $mode={mode}>
        <StyleModalTitle id="add-new-style-title">Add New Style</StyleModalTitle>
        <StyleModalCloseButton
          type="button"
          aria-label="Close Add New Style"
          onClick={onClose}
        >
          <InlineSVG
            src="/assets/style-guide/settings/settings-close-32.svg"
            aria-hidden="true"
          />
        </StyleModalCloseButton>
        <AddStyleIntro>
          <h3>Share a sample of your writing</h3>
          <p>We’ll use it to create style rules so Sudowrite writes the way you want. We don’t train on your writing.</p>
        </AddStyleIntro>
        <UploadDropZone
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
        >
          <UploadDropContent>
            <InlineSVG
              src="/assets/import-icon.svg"
              aria-hidden="true"
            />
            <p className="drop-title">Drag your file here</p>
            <p className="drop-types">
              {isConvertingSample && "Converting file..."}
              {!isConvertingSample && sampleFileName && `${sampleFileName} is ready`}
              {!isConvertingSample && !sampleFileName && "TEXT, DOC, DOCX, RTF, ODT"}
            </p>
            {sampleError && <UploadError role="status">{sampleError}</UploadError>}
            <UploadDivider>OR</UploadDivider>
            <ModalInlineActions>
              <StyleOutlineButton
                type="button"
                disabled={isConvertingSample}
                onClick={() => fileInputRef.current?.click()}
              >
                Browse Files
              </StyleOutlineButton>
              <StyleOutlineButton
                type="button"
                disabled={isConvertingSample}
                onClick={onPasteText}
              >
                Paste Text
              </StyleOutlineButton>
            </ModalInlineActions>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.doc,.docx,.rtf,.odt,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/rtf,application/vnd.oasis.opendocument.text"
              hidden
              onChange={handleFileChange}
            />
          </UploadDropContent>
        </UploadDropZone>
        <StyleModalFooter>
          <StyleFooterSkipButton
            type="button"
            onClick={onSkip}
          >
            Skip
          </StyleFooterSkipButton>
          <StyleContinueButton
            type="button"
            disabled={isConvertingSample || !trimmedSampleText}
            onClick={continueWithSample}
          >
            Continue
          </StyleContinueButton>
        </StyleModalFooter>
      </AddStyleModalSurface>
    </StyleModalOverlay>
  );

  if (mode === "embedded" || typeof document === "undefined") {
    return content;
  }

  return ReactDOM.createPortal(content, document.body);
}

const StyleModalOverlay = styled.div<{ $mode: "modal" | "embedded" }>`
  position: ${({ $mode }) => ($mode === "embedded" ? "absolute" : "fixed")};
  inset: 0;
  z-index: 1000000000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

const StyleModalSurface = styled.div<{ $mode: "modal" | "embedded" }>`
  position: relative;
  width: ${({ $mode }) => ($mode === "embedded" ? "100%" : "min(1000px, calc(100vw - 48px))")};
  min-width: ${({ $mode }) => ($mode === "embedded" ? "0" : "min(720px, calc(100vw - 48px))")};
  height: ${({ $mode }) => ($mode === "embedded" ? "100%" : "min(746px, calc(100vh - 48px))")};
  padding: 24px;
  box-sizing: border-box;
  border-radius: ${({ $mode }) => ($mode === "embedded" ? "20px" : "8px")};
  background: var(--menu-color);
  color: var(--grey-0);
  text-align: left;
`;

const AddStyleModalSurface = styled(StyleModalSurface)`
  display: flex;
  flex-direction: column;
`;

const StyleModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  line-height: 29.0455px;
`;

const StyleModalCloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  svg {
    width: 32px;
    height: 32px;
  }

  path {
    fill: currentColor;
  }

  &:hover,
  &:focus-visible {
    background: var(--menu-color-hover);
    color: var(--grey-0);
    outline: none;
  }
`;

const AddStyleIntro = styled.div`
  margin-top: 33.9545px;

  h3 {
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: 500;
    line-height: 24.2045px;
  }

  p {
    margin: 0;
    color: var(--grey-0);
    font-size: 16px;
    font-weight: 400;
    line-height: 19.3636px;
  }
`;

const UploadDropZone = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 32px;
  border: 1px dashed var(--grey-2);
  border-radius: 8px;
  background: var(--menu-color-hover);
`;

const UploadDropContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 24px;
    color: var(--grey-0);
  }

  path {
    fill: currentColor;
  }

  .drop-title {
    margin: 0 0 16px;
    color: var(--grey-0);
    font-size: 20px;
    font-weight: 500;
    line-height: 24.2045px;
  }

  .drop-types {
    margin: 0 0 40px;
    color: var(--grey-1);
    font-size: 16px;
    font-weight: 500;
    line-height: 19.3636px;
  }
`;

const UploadDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 38px;
  color: var(--grey-0);
  font-size: 16px;
  font-weight: 500;
  line-height: 19.3636px;

  &::before,
  &::after {
    content: "";
    width: 100px;
    height: 1px;
    background: var(--grey-3);
  }
`;

const UploadError = styled.p`
  max-width: 420px;
  margin: -24px 0 20px;
  color: var(--danger-color);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`;

const ModalInlineActions = styled.div`
  display: flex;
  gap: 12px;

  & > button:first-child {
    min-width: 129px;
  }
`;

const StyleOutlineButton = styled(StyleGuideSecondaryButton)`
  min-width: 113px;
`;

const StyleFooterSkipButton = styled(StyleOutlineButton)`
  min-width: 66px;
`;

const StyleModalFooter = styled.div`
  display: flex;
  align-self: flex-end;
  gap: 8px;
  margin-top: 32px;
`;

const StyleContinueButton = styled(StyleGuideSecondaryButton)`
  min-width: 102px;
`;
